import axios from 'axios';
import { getOrCreateMobileDeviceId } from '../utils/mobileDeviceId';
import { reportAxiosError } from './clientErrorReporter';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests (for Capacitor persistent sessions)
});

// Request interceptor for authentication and logging
api.interceptors.request.use(
  async (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Ensure credentials are sent with every request (for cookies)
    config.withCredentials = true;
    
    // Add token to requests if available (fallback for localStorage)
    // Cookies will be sent automatically if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const deviceAwarePaths = [
      '/auth/verify',
      '/mobile-attendance/status',
      '/mobile-attendance/check-in',
      '/mobile-attendance/check-out',
      '/mobile-attendance/today',
      '/device-security/request',
      '/device-security/my-status',
      '/device-security/state',
      '/device-security/my-devices',
      '/device-security/device-change'
    ];
    if (deviceAwarePaths.some((path) => config.url?.includes(path))) {
      const deviceId = await getOrCreateMobileDeviceId();
      if (deviceId) {
        config.headers['x-device-id'] = deviceId;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    
    // List of endpoints that should NOT trigger automatic logout
    // These are user-facing actions where errors should just be shown as toast messages
    const noLogoutEndpoints = [
      '/mobile-attendance/check-in',
      '/mobile-attendance/check-out',
      '/mobile-attendance/status',
      '/mobile-attendance/today',
      '/checkout-requests/submit',
      '/leave-requests/submit',
      '/team-summary/weekly/trigger',
      '/team-summary/monthly/trigger',
      '/feedback',
      '/feedback/',
      '/tasks',
    ];
    
    // Check if this error is from an endpoint that shouldn't trigger logout
    // Use includes to match any part of the URL path
    const shouldSkipLogout = noLogoutEndpoints.some(endpoint => {
      // Remove query params and hash for matching
      const urlPath = url.split('?')[0].split('#')[0];
      return urlPath.includes(endpoint);
    });
    
    // Handle unauthorized/forbidden globally (except for specific endpoints)
    if ((status === 401 || status === 403) && !shouldSkipLogout) {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (_) {}
      // Avoid redirect loops if already on login
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }

    reportAxiosError(error);
    
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  switchToAdmin: (email) => api.post('/auth/switch-to-admin', { email }),
  switchToUser: (email) => api.post('/auth/switch-to-user', { email }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getHealth: () => api.get('/health'),
};

// Users API
export const usersAPI = {
  getUsers: (params = {}) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getDepartments: () => api.get('/users/departments/list'),
  getDepartmentsWithCount: () => api.get('/users/departments'),
  createDepartment: (data) => api.post('/users/departments', data),
  updateDepartment: (oldName, data) => api.put(`/users/departments/${encodeURIComponent(oldName)}`, data),
  deleteDepartment: (name) => api.delete(`/users/departments/${encodeURIComponent(name)}`),
};

export const userProfileAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data)
};

// Attendance API
export const attendanceAPI = {
  getAttendanceLogs: (params = {}) => api.get('/attendance', { params }),
  getAttendanceSummary: (userId, params = {}) => api.get(`/attendance/summary/${userId}`, { params }),
  createAttendanceLog: (logData) => api.post('/attendance', logData),
  updateAttendanceLog: (id, logData) => api.put(`/attendance/${id}`, logData),
  deleteAttendanceLog: (id) => api.delete(`/attendance/${id}`),
  getDailyAttendance: (date) => api.get(`/attendance/daily/${date}`),
  sendTeamSummaryEmail: (data) => api.post('/attendance/team-summary/email', data),
};

// Export API
export const exportAPI = {
  exportCSV: (params = {}) => api.get('/export/csv', { 
    params,
    responseType: 'blob'
  }),
  exportExcel: (params = {}) => api.get('/export/excel', { 
    params,
    responseType: 'blob'
  }),
  exportDailyHoursExcel: (params = {}) => api.get('/export/daily-hours/excel', { 
    params,
    responseType: 'blob'
  }),
};

// Workload API - Manager Dashboard (supports both ClickUp and Orbit)
export const workloadAPI = {
  getMembers: () => api.get('/clickup/team-workload/members'), // Will be replaced with smart routing
  getMembersOrbit: () => api.get('/orbit/team-workload/members'),
  getMemberTasks: (memberId) => api.get(`/clickup/team-workload/user/${memberId}`), // Will be replaced with smart routing
  getMemberTasksOrbit: (memberId) => api.get(`/orbit/team-workload/user/${memberId}`),
  getMyWorkload: () => api.get('/clickup/my-workload'), // Will be replaced with smart routing
  getMyWorkloadOrbit: () => api.get('/orbit/my-workload'),
  updateTask: (taskId, data) => api.put(`/clickup/tasks/${taskId}`, data),
  updateTaskOrbit: (taskId, data) => api.put(`/orbit/tasks/${taskId}`, data),
};

// Policies API
export const policiesAPI = {
  // Working Hours Policies
  getWorkingHoursPolicies: () => api.get('/policies/working-hours'),
  getWorkingHoursPolicy: (id) => api.get(`/policies/working-hours/${id}`),
  createWorkingHoursPolicy: (policyData) => api.post('/policies/working-hours', policyData),
  updateWorkingHoursPolicy: (id, policyData) => api.put(`/policies/working-hours/${id}`, policyData),
  deleteWorkingHoursPolicy: (id) => api.delete(`/policies/working-hours/${id}`),
  
  // Holidays
  getHolidays: (params = {}) => api.get('/policies/holidays', { params }),
  getHoliday: (id) => api.get(`/policies/holidays/${id}`),
  createHoliday: (holidayData) => api.post('/policies/holidays', holidayData),
  updateHoliday: (id, holidayData) => api.put(`/policies/holidays/${id}`, holidayData),
  deleteHoliday: (id) => api.delete(`/policies/holidays/${id}`),
  getUpcomingHolidays: () => api.get('/policies/holidays/upcoming'),
};

export const settingsAPI = {
  getAttendanceAccess: () => api.get('/settings/attendance-access'),
  updateAttendanceAccess: (data) => api.put('/settings/attendance-access', data),
};

// Login Logs API
export const loginLogsAPI = {
  getLogs: (params = {}) => api.get('/login-logs', { params }),
  getStats: (params = {}) => api.get('/login-logs/stats', { params }),
  getUserLogs: (userId, params = {}) => api.get(`/login-logs/user/${userId}`, { params }),
};

export const emailLogsAPI = {
  getLogs: (params = {}) => api.get('/email-logs', { params }),
  getLog: (id) => api.get(`/email-logs/${id}`)
};

// Announcements API
export const announcementsAPI = {
  getAnnouncements: (params = {}) => api.get('/announcements', { params }),
  getUnreadCount: () => api.get('/announcements/unread-count'),
  getAnnouncementById: (id) => api.get(`/announcements/${id}`),
  markAsRead: (id) => api.post(`/announcements/${id}/read`),
  createAnnouncement: (data) => api.post('/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/announcements/${id}`),
  uploadAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/announcements/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getAllForAdmin: () => api.get('/announcements/admin/all'),
};

// Feedback API
export const feedbackAPI = {
  submitFeedback: (data) => api.post('/feedback', data),
  getFeedback: (params = {}) => api.get('/feedback', { params }),
  getFeedbackById: (id) => api.get(`/feedback/${id}`),
  replyToFeedback: (id, message) => api.post(`/feedback/${id}/reply`, { message }),
  updateStatus: (id, status) => api.put(`/feedback/${id}/status`, { status }),
  getUnreadCount: () => api.get('/feedback/unread-count'),
  markAsRead: (id) => api.put(`/feedback/${id}/mark-read`),
  getMyFeedback: () => api.get('/feedback/my-feedback'),
  getUnreadRepliesCount: () => api.get('/feedback/my-feedback/unread-replies'),
};

// Feature Announcements API
export const featureAnnouncementsAPI = {
  getActive: () => api.get('/feature-announcements/active'),
  markViewed: (id, sessionId) => api.post(`/feature-announcements/${id}/view`, { sessionId }),
  dismiss: (id, permanent, sessionId) => api.post(`/feature-announcements/${id}/dismiss`, { dismissPermanently: permanent, sessionId }),
  getAll: () => api.get('/feature-announcements'),
  create: (data) => api.post('/feature-announcements', data),
  update: (id, data) => api.put(`/feature-announcements/${id}`, data),
  toggle: (id, enabled) => api.put(`/feature-announcements/${id}/toggle`, { enabled }),
  delete: (id) => api.delete(`/feature-announcements/${id}`),
  getAnalytics: (params) => api.get('/feature-announcements/analytics', { params }),
  getStats: (id, params) => api.get(`/feature-announcements/${id}/stats`, { params }),
  export: (params) => api.get('/feature-announcements/export', { params, responseType: 'blob' })
};

// KPI Admin API
export const kpiAPI = {
  listSubmissionsAdmin: (params = {}) => api.get('/kpi/admin/submissions', { params }),
  getSubmissionAdmin: (id) => api.get(`/kpi/admin/submissions/${id}`),
  updateSubmissionStatusAdmin: (id, data) => api.post(`/kpi/admin/submissions/${id}/status`, data),
  // Cycle management
  listCycles: () => api.get('/kpi/cycles'),
  getCycle: (id) => api.get(`/kpi/cycles/${id}`),
  createCycle: (data) => api.post('/kpi/cycles', data),
  updateCycle: (id, data) => api.put(`/kpi/cycles/${id}`, data),
  deleteCycle: (id) => api.delete(`/kpi/cycles/${id}`),
  publishAdmin: (data) => api.post('/kpi/admin/publish', data),
  // Manager-facing
  listTeamSubmissions: (params = {}) => api.get('/kpi/team', { params }),
  submitManagerAssessment: (submissionId, data) => api.post(`/kpi/submissions/${submissionId}/manager-assessment`, data),
  submitAdminAssessment: (submissionId, data) => api.post(`/kpi/submissions/${submissionId}/admin-assessment`, data),
  // Employee-facing
  getOrCreateMySubmission: (cycleId) => api.post('/kpi/submissions', { cycleId }),
  getSubmissionById: (submissionId) => api.get(`/kpi/submissions/${submissionId}`),
  upsertItems: (submissionId, items) => api.put(`/kpi/submissions/${submissionId}/items`, { items }),
  submitSelfAssessment: (submissionId, data) => api.post(`/kpi/submissions/${submissionId}/self-assessment`, data),
  // Templates (admin/manager)
  getTemplateItems: (cycleId) => api.get(`/kpi/cycles/${cycleId}/template-items`),
  setTemplateItems: (cycleId, items) => api.post(`/kpi/cycles/${cycleId}/template-items`, { items }),
};

// Devices API (Super Admin only)
export const devicesAPI = {
  getAllDevices: () => api.get('/devices/all'),
  sendNotification: (data) => api.post('/devices/send-notification', data),
};

export const deviceSecurityAPI = {
  getState: (params = {}) => api.get('/device-security/state', { params }),
  requestUnlock: (data) => api.post('/device-security/request', data),
  getMyStatus: (params = {}) => api.get('/device-security/my-status', { params }),
  getLocks: () => api.get('/device-security/locks'),
  getRequests: (params = {}) => api.get('/device-security/requests', { params }),
  approveRequest: (id, data = {}) => api.post(`/device-security/requests/${id}/approve`, data),
  rejectRequest: (id, data = {}) => api.post(`/device-security/requests/${id}/reject`, data),
  manualUnlock: (id) => api.post(`/device-security/locks/${id}/manual-unlock`),
  getMyDevices: () => api.get('/device-security/my-devices'),
  requestDeviceChange: (data) => api.post('/device-security/device-change', data),
  getRegistrations: (params = {}) => api.get('/device-security/registrations', { params }),
  revokeRegistration: (id, data = {}) => api.post(`/device-security/registrations/${id}/revoke`, data),
};

// Leave Requests API
export const leaveRequestsAPI = {
  submit: (data) => api.post('/leave-requests/submit', data),
  getMyRequests: (params = {}) => api.get('/leave-requests/my-requests', { params }),
  getLimits: (params = {}) => api.get('/leave-requests/limits', { params }),
  cancel: (id) => api.put(`/leave-requests/${id}/cancel`),
  cancelApproved: (id, data) => api.post(`/leave-requests/cancel/${id}`, data),
};

// Checkout Requests API
export const checkoutRequestsAPI = {
  submit: (data) => api.post('/checkout-requests/submit', data),
  getMyRequests: (params = {}) => api.get('/checkout-requests/my-requests', { params }),
  cancel: (id) => api.put(`/checkout-requests/${id}/cancel`),
};

// Extra Hours Requests API
export const extraHoursRequestsAPI = {
  submit: (data) => api.post('/extra-hours-requests/submit', data),
  getMyRequests: (params = {}) => api.get('/extra-hours-requests/my-requests', { params }),
  cancel: (id) => api.put(`/extra-hours-requests/${id}/cancel`),
};

// Attendance Correction Requests API
export const attendanceCorrectionsAPI = {
  submit: (data) => api.post('/attendance-corrections/request', data),
  getMyRequests: (params = {}) => api.get('/attendance-corrections/my-requests', { params }),
  getPending: () => api.get('/attendance-corrections/pending'),
  approve: (id, data) => api.put(`/attendance-corrections/approve/${id}`, data),
  reject: (id, data) => api.put(`/attendance-corrections/reject/${id}`, data),
};

// Utility functions for file downloads
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default api;
